import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Phone, Mail, MapPin, User, UploadCloud, Loader2 } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, Overline } from "@/components/Reveal";
import { SITE, PROJECT_TYPES, BUDGETS, IMAGES } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border border-border px-4 py-3.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary transition-colors duration-300";

const initial = { name: "", phone: "", email: "", city: "", project_type: "", budget: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.project_type) {
      toast.error("Please fill in your name, phone, email, and project type.");
      return;
    }
    setSending(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      files.forEach((f) => data.append("photos", f));
      await axios.post(`${API}/consultations`, data);
      toast.success("Thank you! Your consultation request has been received. We'll be in touch shortly.");
      setForm(initial);
      setFiles([]);
      e.target.reset?.();
    } catch (err) {
      toast.error("Something went wrong sending your request. Please try again or call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <SEO
        title="Contact — Request a Free Consultation | Hive Interiors GTA"
        description="Request a free consultation with Hive Interiors for custom kitchen cabinets, kitchen & bathroom renovations, cabinet refacing and home interiors across the GTA."
      />

      <section className="pt-36 md:pt-44 pb-24 md:pb-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-20">
            <Reveal className="md:col-span-3">
              <Overline>Contact</Overline>
            </Reveal>
            <Reveal delay={0.1} className="md:col-span-9">
              <h1 className="font-heading text-4xl md:text-6xl tracking-tight leading-[1.08]">
                Let's Talk About <span className="italic text-primary">Your Space</span>
              </h1>
              <p className="mt-6 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed">
                Tell us about your project and we'll arrange a free consultation to discuss your needs, ideas, budget, and vision.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <Reveal className="lg:col-span-4">
              <div className="relative overflow-hidden aspect-[4/3] lg:aspect-[4/5] mb-10">
                <img
                  src={IMAGES.contactSide}
                  alt="Premium home interior by Hive Interiors"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              <ul className="space-y-6">
                <li className="flex items-start gap-4">
                  <Phone className="w-5 h-5 text-primary mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground">Phone</p>
                    <a href={SITE.phoneHref} data-testid="contact-phone" className="text-lg font-semibold hover:text-primary transition-colors duration-300">
                      {SITE.phone}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Mail className="w-5 h-5 text-primary mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground">Email</p>
                    <a href={`mailto:${SITE.email}`} data-testid="contact-email" className="text-lg font-semibold hover:text-primary transition-colors duration-300">
                      {SITE.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <MapPin className="w-5 h-5 text-primary mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground">Showroom</p>
                    <p className="text-lg font-semibold leading-snug">{SITE.address}</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <User className="w-5 h-5 text-primary mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground">Director</p>
                    <p className="text-lg font-semibold">{SITE.owner}</p>
                  </div>
                </li>
              </ul>
              <div className="mt-8 flex gap-5">
                {SITE.socials.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`contact-social-${s.label.toLowerCase()}`}
                    className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground hover:text-primary transition-colors duration-300"
                  >
                    {s.label}
                  </a>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.15} className="lg:col-span-8">
              <form
                data-testid="consultation-form"
                onSubmit={submit}
                className="bg-card border border-border p-6 md:p-10 grid grid-cols-1 sm:grid-cols-2 gap-5"
              >
                <div>
                  <label htmlFor="cf-name" className="block text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground mb-2">Name *</label>
                  <input id="cf-name" data-testid="form-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="cf-phone" className="block text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground mb-2">Phone *</label>
                  <input id="cf-phone" data-testid="form-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="(416) 000-0000" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="cf-email" className="block text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground mb-2">Email *</label>
                  <input id="cf-email" data-testid="form-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="cf-city" className="block text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground mb-2">City</label>
                  <input id="cf-city" data-testid="form-city-input" value={form.city} onChange={set("city")} placeholder="e.g. Scarborough" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="cf-type" className="block text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground mb-2">Type of Project *</label>
                  <select id="cf-type" data-testid="form-project-type-select" required value={form.project_type} onChange={set("project_type")} className={`${inputCls} bg-card`}>
                    <option value="" disabled>Select a project type</option>
                    {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="cf-budget" className="block text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground mb-2">Estimated Budget</label>
                  <select id="cf-budget" data-testid="form-budget-select" value={form.budget} onChange={set("budget")} className={`${inputCls} bg-card`}>
                    <option value="">Select a range (optional)</option>
                    {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cf-message" className="block text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground mb-2">Message</label>
                  <textarea id="cf-message" data-testid="form-message-textarea" rows={5} value={form.message} onChange={set("message")} placeholder="Tell us about your space, ideas, and goals…" className={`${inputCls} resize-none`} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cf-photos" className="block text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground mb-2">Project Photos (optional)</label>
                  <label
                    htmlFor="cf-photos"
                    data-testid="form-photo-upload"
                    className="flex items-center gap-4 border border-dashed border-border px-4 py-6 cursor-pointer hover:border-primary transition-colors duration-300"
                  >
                    <UploadCloud className="w-6 h-6 text-primary shrink-0" strokeWidth={1.5} />
                    <span className="text-sm text-muted-foreground">
                      {files.length ? `${files.length} photo(s) selected` : "Upload photos of your space (up to 5)"}
                    </span>
                  </label>
                  <input
                    id="cf-photos"
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 5))}
                  />
                </div>
                <div className="sm:col-span-2 pt-2">
                  <button
                    type="submit"
                    data-testid="form-submit-button"
                    disabled={sending}
                    className="inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold transition-colors duration-300 hover:bg-accent hover:text-accent-foreground disabled:opacity-60 group"
                  >
                    {sending ? (
                      <>Sending <Loader2 className="w-4 h-4 animate-spin" /></>
                    ) : (
                      <>Request a Free Consultation <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" /></>
                    )}
                  </button>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
